import React from 'react';
import {Node} from 'reactflow';

import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {TaskSchemaNodeRowData} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {SchemaInfo} from 'processes/modals/field-mapping/FieldMappingConstants';
import {generateTaskSchemaFieldRowIdentifier} from 'processes/modals/field-mapping/transition-modal-with-flowchart-editor/flowchartIdentifierUtils';
import {TaskSchemaNodeSection} from 'processes/modals/field-mapping/transition-modal-with-flowchart-editor/TransitionModalWithFlowchartEditorConstants';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

import DefaultTaskSchemaNodeDetailsSidepanelContent from './DefaultTaskSchemaNodeDetailsSidepanelContent';
import {
  ProcessFlowchartNodeAdapter,
  TaskDefinition,
  TaskSchemaNodeRendererSection,
} from './ProcessFlowchartNodeAdapter';

export interface DefaultNodeAdapterOptions {
  isFlowchartOutput?: boolean;
}

interface TaskSchemaSectionMetadata {
  sectionName: TaskSchemaNodeSection;
  fields: Array<SchemaField>;
  isFlowchartTaskSchema: boolean;
  shouldSkipSection?: boolean;
}

// This function is purely a helper for extracting field icons that need SchemaFieldSelectors
export function maybeGetDefaultSchemaFieldSelectors(field: SchemaField): Array<SchemaFieldSelector> {
  if (field.schemaFieldSelectorId) {
    return [
      {
        name: field.name,
        id: field.schemaFieldSelectorId,

        // These attributes aren't used
        schemaFieldSelectorOptions: [],
        allSchemaFieldSelectorOptions: [],
      },
    ];
  }

  return [];
}

/**
 * Default TaskSchema node adapter, to be used in ReactFlow flowcharts. Converts task schema data
 * into a sectioned node data shape.
 */
export class DefaultTaskSchemaNodeAdapter extends ProcessFlowchartNodeAdapter<DefaultNodeAdapterOptions> {
  getSectionMetadata(schemaInfo: SchemaInfo): Array<TaskSchemaSectionMetadata> {
    const {isFlowchartOutput} = this.options;
    const isFlowchartTaskSchema = schemaInfo.taskSchema.executionType === ProcessExecutionType.FLOWCHART;

    const sections: Array<TaskSchemaSectionMetadata> = [
      {
        sectionName: TaskSchemaNodeSection.INPUTS,
        fields: schemaInfo.taskSchema.fields,
        shouldSkipSection: isFlowchartTaskSchema && isFlowchartOutput,
        isFlowchartTaskSchema,
      },
    ];

    if (schemaInfo.taskSchema.outputSchema) {
      sections.push({
        sectionName: TaskSchemaNodeSection.OUTPUTS,
        fields: schemaInfo.taskSchema.outputSchema.fields,
        shouldSkipSection: isFlowchartTaskSchema && !isFlowchartOutput,
        isFlowchartTaskSchema,
      });
    }

    return sections;
  }

  createSectionRows(metadata: TaskSchemaSectionMetadata): Array<TaskSchemaNodeRowData> {
    const {fields, sectionName, isFlowchartTaskSchema} = metadata;
    return fields.map((field) => ({
      id: generateTaskSchemaFieldRowIdentifier(field.apiId, sectionName),
      fieldName: field.name,
      fieldIcon: getIconAndLabel({field, schemaFieldSelectors: maybeGetDefaultSchemaFieldSelectors(field)})
        .icon,
      sectionName,
      isFlowchartTask: isFlowchartTaskSchema,
    }));
  }

  createSections(taskDefinition: TaskDefinition): Array<TaskSchemaNodeRendererSection> {
    const {schemaInfo} = taskDefinition;
    const sectionMetadatas = this.getSectionMetadata(schemaInfo);

    return sectionMetadatas
      .filter((metadata) => !metadata.shouldSkipSection)
      .map((metadata) => ({
        sectionName: metadata.sectionName,
        sectionRows: this.createSectionRows(metadata),
      }));
  }

  nodeDetailsSidepanelContent(node: Node): JSX.Element {
    return <DefaultTaskSchemaNodeDetailsSidepanelContent selectedNode={node} />;
  }
}
